import type {
  Document,
  Chunk,
  ChunkingConfig,
  ChunkingStrategy,
} from '../types';
import { DocumentProcessingError } from '../types';
import {
  generateId,
  estimateTokens,
  createOverlappingChunks,
  findSentenceBoundaries,
  findParagraphBoundaries,
  extractHeaders,
  splitOnWordBoundary,
  isValidContent,
} from '../core/utils';

/**
 * Document chunking service that supports multiple strategies
 */
export class DocumentChunker {
  /**
   * Chunk a document using the specified strategy
   */
  async chunkDocument(document: Document, config: ChunkingConfig): Promise<Chunk[]> {
    try {
      switch (config.strategy) {
        case 'fixed_size':
          return this.fixedSizeChunking(document, config);
        case 'semantic':
          return this.semanticChunking(document, config);
        case 'recursive':
          return this.recursiveChunking(document, config);
        case 'sentence':
          return this.sentenceChunking(document, config);
        case 'paragraph':
          return this.paragraphChunking(document, config);
        case 'custom':
          if (config.customSplitter) {
            return config.customSplitter(document.content, config);
          }
          throw new Error('Custom splitter not provided');
        default:
          throw new Error(`Unsupported chunking strategy: ${config.strategy}`);
      }
    } catch (error) {
      throw new DocumentProcessingError(
        document.id,
        `Chunking failed: ${error}`,
        error as Error
      );
    }
  }

  /**
   * Chunk multiple documents
   */
  async chunkDocuments(documents: Document[], config: ChunkingConfig): Promise<Chunk[]> {
    const allChunks: Chunk[] = [];
    
    for (const document of documents) {
      try {
        const chunks = await this.chunkDocument(document, config);
        allChunks.push(...chunks);
      } catch (error) {
        console.error(`Failed to chunk document ${document.id}:`, error);
        // Continue with other documents
      }
    }
    
    return allChunks;
  }

  /**
   * Fixed-size chunking with overlap
   */
  private fixedSizeChunking(document: Document, config: ChunkingConfig): Chunk[] {
    const { content } = document;
    const chunks = createOverlappingChunks(content, config.size, config.overlap);
    
    return chunks
      .map((chunk, index) => this.createChunk(
        chunk.content,
        document,
        index,
        chunk.startIndex,
        chunk.endIndex
      ))
      .filter(chunk => this.isValidChunk(chunk, config));
  }

  /**
   * Semantic chunking based on content structure
   */
  private semanticChunking(document: Document, config: ChunkingConfig): Chunk[] {
    const { content } = document;
    const headers = extractHeaders(content);
    
    if (headers.length === 0) {
      // Fall back to paragraph chunking if no headers found
      return this.paragraphChunking(document, config);
    }
    
    const chunks: Chunk[] = [];
    const lines = content.split('\n');
    let currentSection = '';
    let currentStartIndex = 0;
    let chunkIndex = 0;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isHeader = /^#{1,6}\s+/.test(line);
      
      if (isHeader && currentSection.length > 0) {
        // End current section and create chunk
        const sectionContent = currentSection.trim();
        if (sectionContent.length >= (config.minChunkSize || 50)) {
          const chunk = this.createChunk(
            sectionContent,
            document,
            chunkIndex++,
            currentStartIndex,
            currentStartIndex + currentSection.length
          );
          
          if (this.isValidChunk(chunk, config)) {
            chunks.push(chunk);
          }
        }
        
        // Start new section
        currentSection = line + '\n';
        currentStartIndex = content.indexOf(currentSection, currentStartIndex);
      } else {
        currentSection += line + '\n';
      }
    }
    
    // Handle last section
    if (currentSection.trim().length >= (config.minChunkSize || 50)) {
      const chunk = this.createChunk(
        currentSection.trim(),
        document,
        chunkIndex,
        currentStartIndex,
        currentStartIndex + currentSection.length
      );
      
      if (this.isValidChunk(chunk, config)) {
        chunks.push(chunk);
      }
    }
    
    // If chunks are too large, split them further
    return this.splitLargeChunks(chunks, config);
  }

  /**
   * Recursive chunking with hierarchical splitting
   */
  private recursiveChunking(document: Document, config: ChunkingConfig): Chunk[] {
    const separators = config.separators || [
      '\n\n',    // Paragraphs
      '\n',      // Lines
      '. ',      // Sentences
      ' ',       // Words
      '',        // Characters
    ];
    
    return this.recursiveSplit(document.content, separators, document, config);
  }

  /**
   * Recursively split text using hierarchical separators
   */
  private recursiveSplit(
    text: string,
    separators: string[],
    document: Document,
    config: ChunkingConfig,
    currentIndex: number = 0
  ): Chunk[] {
    if (text.length <= config.size || separators.length === 0) {
      const chunk = this.createChunk(text, document, 0, currentIndex, currentIndex + text.length);
      return this.isValidChunk(chunk, config) ? [chunk] : [];
    }
    
    const separator = separators[0];
    const parts = separator === '' ? text.split('') : text.split(separator);
    
    if (parts.length === 1) {
      // Try next separator
      return this.recursiveSplit(text, separators.slice(1), document, config, currentIndex);
    }
    
    const chunks: Chunk[] = [];
    let currentText = '';
    let currentStart = currentIndex;
    let chunkIndex = 0;
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const testText = currentText + (currentText ? separator : '') + part;
      
      if (testText.length <= config.size) {
        currentText = testText;
      } else {
        // Current text is full, create chunk
        if (currentText.trim().length > 0) {
          const chunk = this.createChunk(
            currentText.trim(),
            document,
            chunkIndex++,
            currentStart,
            currentStart + currentText.length
          );
          
          if (this.isValidChunk(chunk, config)) {
            chunks.push(chunk);
          }
        }
        
        // Start new chunk with current part
        currentStart += currentText.length + (currentText ? separator.length : 0);
        currentText = part;
        
        // If single part is too large, split it further
        if (part.length > config.size) {
          const subChunks = this.recursiveSplit(
            part,
            separators.slice(1),
            document,
            config,
            currentStart
          );
          chunks.push(...subChunks);
          currentText = '';
          currentStart += part.length;
        }
      }
    }
    
    // Handle remaining text
    if (currentText.trim().length > 0) {
      const chunk = this.createChunk(
        currentText.trim(),
        document,
        chunkIndex,
        currentStart,
        currentStart + currentText.length
      );
      
      if (this.isValidChunk(chunk, config)) {
        chunks.push(chunk);
      }
    }
    
    return chunks;
  }

  /**
   * Sentence-based chunking
   */
  private sentenceChunking(document: Document, config: ChunkingConfig): Chunk[] {
    const { content } = document;
    const boundaries = findSentenceBoundaries(content);
    
    const chunks: Chunk[] = [];
    let currentChunk = '';
    let currentStart = 0;
    let chunkIndex = 0;
    
    for (let i = 1; i < boundaries.length; i++) {
      const sentenceStart = boundaries[i - 1];
      const sentenceEnd = boundaries[i];
      const sentence = content.slice(sentenceStart, sentenceEnd).trim();
      
      if (!sentence) continue;
      
      const testChunk = currentChunk + (currentChunk ? ' ' : '') + sentence;
      
      if (testChunk.length <= config.size) {
        currentChunk = testChunk;
      } else {
        // Create chunk from accumulated sentences
        if (currentChunk) {
          const chunk = this.createChunk(
            currentChunk,
            document,
            chunkIndex++,
            currentStart,
            currentStart + currentChunk.length
          );
          
          if (this.isValidChunk(chunk, config)) {
            chunks.push(chunk);
          }
        }
        
        // Start new chunk
        currentStart = sentenceStart;
        currentChunk = sentence;
        
        // If single sentence is too large, split it
        if (sentence.length > config.size) {
          const wordChunks = splitOnWordBoundary(sentence, config.size);
          for (const wordChunk of wordChunks) {
            const chunk = this.createChunk(
              wordChunk,
              document,
              chunkIndex++,
              currentStart,
              currentStart + wordChunk.length
            );
            
            if (this.isValidChunk(chunk, config)) {
              chunks.push(chunk);
            }
            currentStart += wordChunk.length;
          }
          currentChunk = '';
        }
      }
    }
    
    // Handle remaining chunk
    if (currentChunk) {
      const chunk = this.createChunk(
        currentChunk,
        document,
        chunkIndex,
        currentStart,
        currentStart + currentChunk.length
      );
      
      if (this.isValidChunk(chunk, config)) {
        chunks.push(chunk);
      }
    }
    
    return chunks;
  }

  /**
   * Paragraph-based chunking
   */
  private paragraphChunking(document: Document, config: ChunkingConfig): Chunk[] {
    const { content } = document;
    const boundaries = findParagraphBoundaries(content);
    
    const chunks: Chunk[] = [];
    let currentChunk = '';
    let currentStart = 0;
    let chunkIndex = 0;
    
    for (let i = 1; i < boundaries.length; i++) {
      const paragraphStart = boundaries[i - 1];
      const paragraphEnd = boundaries[i];
      const paragraph = content.slice(paragraphStart, paragraphEnd).trim();
      
      if (!paragraph) continue;
      
      const testChunk = currentChunk + (currentChunk ? '\n\n' : '') + paragraph;
      
      if (testChunk.length <= config.size) {
        currentChunk = testChunk;
      } else {
        // Create chunk from accumulated paragraphs
        if (currentChunk) {
          const chunk = this.createChunk(
            currentChunk,
            document,
            chunkIndex++,
            currentStart,
            currentStart + currentChunk.length
          );
          
          if (this.isValidChunk(chunk, config)) {
            chunks.push(chunk);
          }
        }
        
        // Start new chunk
        currentStart = paragraphStart;
        currentChunk = paragraph;
        
        // If single paragraph is too large, split it further
        if (paragraph.length > config.size) {
          const subChunks = this.fixedSizeChunking(
            { ...document, content: paragraph },
            { ...config, strategy: 'fixed_size' }
          );
          
          chunks.push(...subChunks.map(chunk => ({
            ...chunk,
            metadata: {
              ...chunk.metadata,
              documentId: document.id,
            },
          })));
          
          currentChunk = '';
          currentStart += paragraph.length;
        }
      }
    }
    
    // Handle remaining chunk
    if (currentChunk) {
      const chunk = this.createChunk(
        currentChunk,
        document,
        chunkIndex,
        currentStart,
        currentStart + currentChunk.length
      );
      
      if (this.isValidChunk(chunk, config)) {
        chunks.push(chunk);
      }
    }
    
    return chunks;
  }

  /**
   * Split chunks that are too large
   */
  private splitLargeChunks(chunks: Chunk[], config: ChunkingConfig): Chunk[] {
    const result: Chunk[] = [];
    
    for (const chunk of chunks) {
      if (chunk.content.length <= config.maxChunkSize!) {
        result.push(chunk);
      } else {
        // Split large chunk using fixed-size strategy
        const subChunks = createOverlappingChunks(
          chunk.content,
          config.size,
          config.overlap
        );
        
        let subIndex = 0;
        for (const subChunk of subChunks) {
          const newChunk = this.createChunk(
            subChunk.content,
            { id: chunk.metadata.documentId } as Document,
            chunk.metadata.index + subIndex / 1000, // Maintain ordering
            chunk.metadata.startIndex + subChunk.startIndex,
            chunk.metadata.startIndex + subChunk.endIndex,
            {
              ...chunk.metadata,
              chunkId: `${chunk.id}_${subIndex}`,
            }
          );
          
          if (this.isValidChunk(newChunk, config)) {
            result.push(newChunk);
          }
          subIndex++;
        }
      }
    }
    
    return result;
  }

  /**
   * Create a chunk object
   */
  private createChunk(
    content: string,
    document: Document,
    index: number,
    startIndex: number,
    endIndex: number,
    metadataOverride?: any
  ): Chunk {
    const chunkId = generateId('chunk');
    const headers = extractHeaders(content);
    
    return {
      id: chunkId,
      content: content.trim(),
      tokens: estimateTokens(content),
      metadata: {
        chunkId,
        documentId: document.id,
        index,
        startIndex,
        endIndex,
        title: document.metadata.title,
        section: headers[0] || undefined,
        headers: headers.length > 0 ? headers : undefined,
        ...metadataOverride,
      },
    };
  }

  /**
   * Validate that a chunk meets the configuration requirements
   */
  private isValidChunk(chunk: Chunk, config: ChunkingConfig): boolean {
    const contentLength = chunk.content.length;
    
    // Check minimum size
    if (config.minChunkSize && contentLength < config.minChunkSize) {
      return false;
    }
    
    // Check maximum size
    if (config.maxChunkSize && contentLength > config.maxChunkSize) {
      return false;
    }
    
    // Check if content is valid (not empty/whitespace)
    if (!isValidContent(chunk.content)) {
      return false;
    }
    
    return true;
  }

  /**
   * Get default chunking configuration
   */
  static getDefaultConfig(): ChunkingConfig {
    return {
      strategy: 'recursive',
      size: 512,
      overlap: 128,
      minChunkSize: 50,
      maxChunkSize: 2048,
      preserveStructure: false,
      separators: ['\n\n', '\n', '. ', ' ', ''],
    };
  }

  /**
   * Get optimized configuration for specific document types
   */
  static getOptimizedConfig(documentFormat: string): ChunkingConfig {
    const baseConfig = DocumentChunker.getDefaultConfig();
    
    switch (documentFormat) {
      case 'markdown':
        return {
          ...baseConfig,
          strategy: 'semantic',
          preserveStructure: true,
          separators: ['\n## ', '\n# ', '\n\n', '\n', '. ', ' ', ''],
        };
        
      case 'html':
        return {
          ...baseConfig,
          strategy: 'semantic',
          preserveStructure: true,
        };
        
      case 'pdf':
        return {
          ...baseConfig,
          strategy: 'paragraph',
          size: 768,
          overlap: 150,
        };
        
      case 'csv':
      case 'json':
        return {
          ...baseConfig,
          strategy: 'fixed_size',
          size: 256,
          overlap: 50,
        };
        
      case 'txt':
      default:
        return {
          ...baseConfig,
          strategy: 'recursive',
        };
    }
  }
}